import { http } from "./client";
import type { PageParams } from "@/types/api";
import type {
  ClearanceTaskStatus, DocumentRecord, EmployeeResignation, EmployeeSeparation, ExitDashboardStats,
  ExitDocumentTemplate, ExitDocumentType, ExitInterview, ExitInterviewAnalytics, FinalSettlement,
  FinalSettlementRevision, KnowledgeTransferNote, ResignationStatus, SeparationStatus, SeparationType,
} from "@/types/domain";

export interface ResignationFilters extends PageParams {
  employee_id?: string;
  status?: ResignationStatus | "";
}

export interface SeparationFilters extends PageParams {
  employee_id?: string;
  status?: SeparationStatus | "";
}

export interface SubmitResignationInput {
  resignation_date: string;
  requested_last_working_day: string;
  reason?: string;
}

export interface DecideResignationInput {
  decision: "approved" | "rejected";
  final_last_working_day?: string;
  notice_waived?: boolean;
  override_reason?: string;
  note?: string;
}

export interface ManagerDecideResignationInput {
  decision: "approved" | "rejected";
  remarks?: string;
}

export interface InitiateSeparationInput {
  separation_type: SeparationType;
  last_working_day: string;
  reason: string;
  hr_owner_id?: string;
}

export interface ExitInterviewInput {
  reason_for_leaving?: string;
  manager_relationship_rating?: number;
  work_environment_rating?: number;
  compensation_satisfaction_rating?: number;
  career_growth_rating?: number;
  suggestions?: string;
  rehire_eligible?: boolean;
  additional_comments?: string;
}

export const resignationsApi = {
  submit: (employeeId: string, input: SubmitResignationInput) =>
    http.post<EmployeeResignation>(`/employees/${employeeId}/resignation`, input),
  list: (filters: ResignationFilters = {}) => http.getPage<EmployeeResignation>("/resignations", filters),
  get: (id: string) => http.get<EmployeeResignation>(`/resignations/${id}`),
  decide: (id: string, input: DecideResignationInput) =>
    http.post<{ resignation: EmployeeResignation; separation: EmployeeSeparation | null }>(`/resignations/${id}/decide`, input),
  managerDecide: (id: string, input: ManagerDecideResignationInput) =>
    http.post<EmployeeResignation>(`/resignations/${id}/manager-decide`, input),
  withdraw: (id: string, note?: string) => http.post<EmployeeResignation>(`/resignations/${id}/withdraw`, { note }),
};

export const separationsApi = {
  initiate: (employeeId: string, input: InitiateSeparationInput) =>
    http.post<EmployeeSeparation>(`/employees/${employeeId}/separation`, input),
  list: (filters: SeparationFilters = {}) => http.getPage<EmployeeSeparation>("/separations", filters),
  get: (id: string) => http.get<EmployeeSeparation>(`/separations/${id}`),
  approve: (id: string) => http.post<EmployeeSeparation>(`/separations/${id}/approve`, {}),
  reject: (id: string, reason: string) => http.post<EmployeeSeparation>(`/separations/${id}/reject`, { reason }),
  cancel: (id: string, reason: string) => http.post<EmployeeSeparation>(`/separations/${id}/cancel`, { reason }),
  confirmLeaveClearance: (id: string) => http.post<EmployeeSeparation>(`/separations/${id}/leave-clearance`, {}),
  revokeAccess: (id: string) => http.post<EmployeeSeparation>(`/separations/${id}/revoke-access`, {}),
  moveToSettlement: (id: string) => http.post<EmployeeSeparation>(`/separations/${id}/move-to-settlement`, {}),
  complete: (id: string) => http.post<EmployeeSeparation>(`/separations/${id}/complete`, {}),
  submitExitInterview: (id: string, input: ExitInterviewInput) =>
    http.post<ExitInterview>(`/separations/${id}/exit-interview`, input),
  getExitInterview: (id: string) => http.get<ExitInterview>(`/separations/${id}/exit-interview`),
  exitInterviewAnalytics: () => http.get<ExitInterviewAnalytics>("/separations/analytics/exit-interviews"),
  dashboardStats: () => http.get<ExitDashboardStats>("/separations/dashboard"),
  updateClearanceTask: (
    id: string, code: string, status: ClearanceTaskStatus, notes?: string, rejectionReason?: string,
  ) =>
    http.post<EmployeeSeparation>(`/separations/${id}/clearance-tasks/${code}`, {
      status, notes, rejection_reason: rejectionReason,
    }),
  assignClearanceTask: (id: string, code: string, assignedUserId: string) =>
    http.post<EmployeeSeparation>(`/separations/${id}/clearance-tasks/${code}/assign`, { assigned_user_id: assignedUserId }),
  saveKnowledgeTransfer: (id: string, note: KnowledgeTransferNote) =>
    http.put<EmployeeSeparation>(`/separations/${id}/knowledge-transfer`, note),
  generateExitDocument: (id: string, templateId: string) =>
    http.post<DocumentRecord>(`/separations/${id}/exit-documents`, { template_id: templateId }),
  myExit: () => http.get<EmployeeSeparation>("/employee/self/exit"),
};

export const exitDocumentTemplatesApi = {
  list: (includeInactive = false) =>
    http.get<ExitDocumentTemplate[]>("/settings/exit-document-templates", { include_inactive: includeInactive }),
  create: (input: { title: string; document_type: ExitDocumentType; body_template: string }) =>
    http.post<ExitDocumentTemplate>("/settings/exit-document-templates", input),
  update: (id: string, input: { title: string; document_type: ExitDocumentType; body_template: string; is_active?: boolean }) =>
    http.put<ExitDocumentTemplate>(`/settings/exit-document-templates/${id}`, input),
};

export const finalSettlementRevisionsApi = {
  reopen: (id: string, reason: string) => http.post<FinalSettlement>(`/final-settlements/${id}/reopen`, { reason }),
  list: (id: string) => http.get<FinalSettlementRevision[]>(`/final-settlements/${id}/revisions`),
  myFinalSettlement: () => http.get<FinalSettlement>("/employee/self/final-settlement"),
};
